import { TranslateService } from '@ngx-translate/core';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  HostListener,
  OnInit,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { DecimalPipe } from '@angular/common';
import { takeUntil } from 'rxjs';
import { inject } from '@angular/core';
import { Injector } from '@angular/core';

import { StorageService } from './core/services/storage.service';
import { PrintingService } from './core/services/printing.service';
import { ThemeService } from '~/core/services/theme.service';
import { resolveCompanyLogo } from './core/helpers/company-logo.utils';
import { AuthenticationService } from './core/services/authentication.service';
import { UtilsService } from './core/helpers/utils';
import { getBrowserLanguage } from './core/helpers/storage-utils';
import { WidgetService } from './core/services/widget.service';
import { Company } from './core/models/company';
import { NavigationService } from './core/services/navigation.service';
import { DepositPollingService } from '~/core/services/deposit-polling.service';
import { FormatOddService } from '~/core/services/format-odd.service';
import { CookieService } from '~/core/services/cookie.service';
import { SportsbookIntegrationService } from './pages/sportsbook-integration/sportsbook-integration.service';
import { KycValidationService } from '~/core/helpers/kyc-validation.service';
import { KYCMethod } from '~/core/enum/kyc.enum';
import { HelperPlus2Service } from '~/core/services/helper-plus2.service';
import { language } from '~/core/constants/constants';
import { LocationUtils } from '~/core/helpers/location-utils';
import { LayoutChoiceDirective } from '~/core/abstracts/layout-choice.directive';
import { MemoryCleanupService } from '~/core/services/memory-cleanup.service';
import { CurrencyCasePipe } from './core/helpers/currency-case-pipe/currency-case-pipe';
import { ApiErrorParser } from './core/helpers/api-error-parser';
import { CasinoService } from './core/services/casino.service';
import { ApiErrorService } from './core/services/api.error.service';
import { DepositService } from './core/services/deposit.service';
import { IndexedDBService } from '~/core/services/indexed-db-storage.service';
import { AnalyticsService } from '~/core/services/analytics.service';
import { DestroyService } from '~/core/services/destroy.service';
import { CompanyStore } from './core/store/company.store';
import { UserStore } from './core/store/user.store';
import { DeferredStarterService } from './core/services/deferred-starter.service';
import { LoadPriorityService } from '~/core/services/loadPriorityService';
import { LoadSecondaryService } from '~/core/services/loadSecondaryService';
import { DialogOrchestratorService } from '~/core/services/dialog-orchestrator.service';
import { SharedBetService } from '~/core/services/shared-bet.service';
// import { LogService } from '~/core/services/log.service';
import { GenericScriptsService } from './core/services/generic-scripts.service';
import { MenuSportService } from './core/services/menu-sport.service';
import { MobileBottomMenuService } from './core/services/mobile-bottom-menu.service';
import { PushService } from './core/services/push.service';
import { getSource, isDesktop } from './core/helpers/device-utils';
import { SpeedLifecycleService } from '~/core/services/speed-lifecycle.service';
import { SeoService } from '~/core/services/seo.service';
import { AuthScreenPreloadService } from '~/core/services/auth-screen-preload.service';
import { OddsBoostService } from '~/components/floating-coupon/odds-boost/odds-boost.service';
import { QuickDepositService } from '~/core/services/quick-deposit.service';
import { PortalSportsbookService } from '~/core/services/portal-sportsbook.service';

declare var xtremepush: any;

declare global {
  interface Window {
    __infoCompany: any;
  }
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [RouterOutlet],
  providers: [
    DestroyService,
    DecimalPipe,
    TranslateService,
    CurrencyCasePipe,
    ApiErrorParser,
    NavigationService,
    CasinoService,
    ApiErrorService,
    DepositPollingService,
    DepositService,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent extends LayoutChoiceDirective implements OnInit, AfterViewInit {
  translate = inject(TranslateService);
  storageService = inject(StorageService);
  private injector = inject(Injector);

  private sharedBetService = inject(SharedBetService);
  private widgetService = inject(WidgetService);
  printingService = inject(PrintingService);
  auth = inject(AuthenticationService);
  dialog = inject(MatDialog);
  private seoService = inject(SeoService);
  private cdr = inject(ChangeDetectorRef);
  private navigation = inject(NavigationService);
  protected depositPolling = inject(DepositPollingService);

  protected router = inject(Router);
  readonly formatOddService = inject(FormatOddService);
  private cookieService = inject(CookieService);
  private readonly sportsbookIntegrationService = inject(SportsbookIntegrationService);
  private kycValidationService = inject(KycValidationService);
  protected readonly portalSportsbookService = inject(PortalSportsbookService);
  protected helper = inject(HelperPlus2Service);
  protected analyticsService = inject(AnalyticsService);
  protected deferredStarter = inject(DeferredStarterService);
  protected menuSportService = inject(MenuSportService);
  protected mobileMenuService = inject(MobileBottomMenuService);
  protected customScriptsService = inject(GenericScriptsService);
  private pushNotification = inject(PushService);

  private loadPriorityService = inject(LoadPriorityService);
  private loadSecondaryService = inject(LoadSecondaryService);
  private dialogOrchestrator = inject(DialogOrchestratorService);
  private authScreenPreload = inject(AuthScreenPreloadService);
  private readonly oddsBoostService = inject(OddsBoostService);
  private readonly quickDepositService = inject(QuickDepositService);

  protected readonly window = window;
  private resizeTimeout: any;
  private lastSource = getSource();
  private destroy$ = inject(DestroyService);
  private readonly companyStore: CompanyStore = inject(CompanyStore);
  private readonly _userStore = inject(UserStore);
  private readonly _isLoggedIn = computed(() => !!this._userStore.userSignal());

  companyLogo: string;

  constructor() {
    super();
    const companyData = this.companyStore.companyInfoSignal();

    this.settingCompany({
      company: companyData.company,
      view_config: companyData.view_config,
      config: companyData.config,
    });

    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.scrollTopGlobal();
        const currentRoute = this.router.routerState.root;
        this.setExternalParamsFromRoute(currentRoute);

        const isEnabled =
          this.companyStore.companySignal().crm?.xtremepush?.enable && this.companyStore.companySignal().crm?.xtremepush?.sdk_key;
        if (isEnabled && typeof xtremepush !== 'undefined') {
          xtremepush('event', 'page_view', document.location);
        }

        const navigation = this.router.getCurrentNavigation();
        if (navigation?.extras?.state?.openLogin) {
          MemoryCleanupService.setTimeout(
            'openLogin',
            () => {
              this.helper.toggleLoginSideMenuEvent.emit();
              window.history.replaceState({}, document.title);
            },
            500,
          );
        }
      }
    });
    this.quickDepositService.setDepositEventsListener();
  }

  ngOnInit(): void {
    super.ngOnInit();
    // Tive que manter fora, pois na versão 'desktop' a rota muda.
    this.loadPriorityService.setAffiliateInfo().then(() => {
      // LogService.log('Load Affiliate Scripts and Send Click', r);
    });
    this.checkLoadBetShare();

    // Carregamento prioritário
    this.deferredStarter.runWhenStable(() => {
      this.createDynamicRoute();
    });

    // Carregamento secundário
    this.deferredStarter.runWhenIdle(() => {
      // Ativa a sincronização entre abas após a inicialização da aplicação
      IndexedDBService.enableCrossTabSync();
      if (isDesktop()) {
        this.authScreenPreload.preloadDesktopAuthScreens();
      } else {
        this.authScreenPreload.preloadMobileRegisterScreens();
      }
      this.loadSecondaryService.loadSecondaryServices();
      this.depositPolling.startCachedTracking();
    });
  }

  private readonly speedService = inject(SpeedLifecycleService);

  ngAfterViewInit() {
    this.deferredStarter.runWhenStable(() => {
      this.appReady();
      this.cdr.markForCheck();
    }, 100);

    this.speedService.onLcpReady$.subscribe(() => {
      this.loadPriorityService.loadPriorityServices();
    });
  }

  checkLoadBetShare() {
    if (window.location.href.includes('bet/share')) {
      const params = Object.fromEntries(new URLSearchParams(window.location.search).entries());
      this.auth.setExternalParams(params);

      const urlSplitted = window.location.href.split('/');
      const betCode = urlSplitted[urlSplitted.length - 1];

      this.sharedBetService.applySharedBet(betCode);
    }
  }

  appReady() {
    if (typeof window !== 'undefined') {
      this.dispatchAppReady();
    }
  }

  private dispatchAppReady() {
    if (typeof window !== 'undefined' && !sessionStorage.getItem('visited')) {
      this.cookieService.set('visited', 'true');
      sessionStorage.setItem('visited', 'true');
      window.dispatchEvent(new Event('app-ready'));
    } else {
      window.dispatchEvent(new Event('app-ready'));
    }
  }

  setCurrentLocation(company: Company) {
    if (company.location_restriction?.enable && navigator.permissions) {
      navigator.permissions.query({ name: UtilsService.NAVIGATOR_GEO_LOCATION as PermissionName }).then((result) => {
        const isPermissionGranted = result.state === UtilsService.NAVIGATOR_GRANTED_LOCATION_STATUS;
        const showLocationModal = this.companyStore.configSignal()?.location?.friendly_modal?.enable;
        if (!isPermissionGranted) {
          if (showLocationModal) {
            import('./containers/dialogs/need-location-dialog/need-location-dialog.component').then(({ NeedLocationDialogComponent }) => {
              this.auth.needLocationModal = this.dialog.open(NeedLocationDialogComponent, {
                ariaModal: true,
                ariaLabelledBy: 'dialog-location',
                disableClose: false,
                width: '405px',
              });
              this.auth.needLocationModal.componentInstance.nextStepEmitter.pipe().subscribe(() => {
                LocationUtils.getCurrentLocation(this.auth);
              });
            });
          } else {
            LocationUtils.getCurrentLocation(this.auth);
          }
        } else {
          LocationUtils.getCurrentLocation(this.auth);
        }
      });
    }
  }

  settingCompany(response) {
    const company = response.company;
    this.updateCompanyDisplayedData(company);
    this.seoService.updateMeta(company);
    this.setLn();

    this.checkIFrame(company);

    this.deferredStarter.runWhenStable(() => {
      this.setOddsFormat();
      this.setCurrentLocation(company);
      this.initKYCMethod();
    });

    this.deferredStarter.runWhenIdle(() => {
      this.menuSportService.prepareMenuList();
      this.mobileMenuService.prepareMenuList();
      this.widgetService.setWidget(company);
      this.pushNotification.setPushNotification(company);
      this.cookieService.initialize(company, this._isLoggedIn());

      this.analyticsService.setAnalytics(company, this._isLoggedIn());
      this.dialogOrchestrator.showMajorityDialog();
      import('./core/services/chat.service').then((m) => {
        this.injector.get(m.ChatService).setChat(company.chat);
      });
      this.checkAffiliateScripts();
      this.customScriptsService.setThirdPartyScripts(response);
    });

    this.userUpdate();
    MemoryCleanupService.setTimeout(
      'detect',
      () => {
        this.cdr.markForCheck();
      },
      0,
    );
    CookieService.deleteRefreshCookieIfExists();
  }

  checkAffiliateScripts() {
    const isLoggedIn = this._isLoggedIn();
    if (!isLoggedIn) {
      this.customScriptsService.updateCompanyScripts(isLoggedIn);
    }
    const affiliatedId = AuthenticationService.affiliated?.id?.split('?')[0];
    if (affiliatedId) {
      const scripts = this.customScriptsService.getScriptsStored();
      this.customScriptsService.updateScriptsRunning(scripts, isLoggedIn);
      this.customScriptsService.removeScriptsStored();
    }
  }

  userUpdate() {
    const hasToken = window.location.search.indexOf('?token=');
    if (hasToken > -1) {
      const token = window.location.search.split('?token=')[1];
      this.auth.storeToken(token);
      this.navigation.closeRequestDeposit();
    }
    this.auth.validateToken();
  }

  setLn() {
    const saveLanguage = (lang: string) => {
      this.cookieService.set('lang', lang);
      StorageService.currentLanguage = lang;
    };

    const languageFromCompany = () => {
      const companyLangKey = this.companyStore.companySignal()?.language;
      return language[companyLangKey as keyof typeof language];
    };

    saveLanguage(this.storageService.getLanguage() || languageFromCompany() || getBrowserLanguage() || language.PT_BR);
  }

  setOddsFormat() {
    const oddsFormat = StorageService.oddFormat;
    if (oddsFormat) {
      StorageService.oddFormat = oddsFormat;
    } else {
      StorageService.oddFormat = this.companyStore.companySignal()?.odds_format || 'DECIMAL';
    }
    this.formatOddService.changeFormat.emit();
  }

  updateCompanyDisplayedData(company: Company) {
    this.companyLogo = resolveCompanyLogo(company, ThemeService.isLightTheme);
  }

  @HostListener('window:resize')
  onWindowResize() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      UtilsService.updateLayoutSize();
      this.menuSportService.prepareMenuList();
      this.mobileMenuService.prepareMenuList();

      const currentSource = getSource();
      if (currentSource !== this.lastSource) {
        window.location.reload();
      }
    }, 200);
  }

  setExternalParamsFromRoute(route: ActivatedRoute) {
    const queryParams = route.snapshot.queryParams;
    const affiliateId = queryParams?.['affiliate_id'] || queryParams?.['id'];

    this.auth.setExternalAffiliate(queryParams?.external_affiliate_id);
    this.auth.setExternalAffiliateTracker(queryParams?.external_affiliate_tracker);

    if (!this.helper.currentURLScreen?.includes('user-dashboard')) {
      this.auth.setExternalParams(queryParams);
    }

    if (affiliateId) {
      this.auth.setAffiliated(JSON.stringify({ id: affiliateId }));
    }
  }

  private async initKYCMethod() {
    if (this.companyStore.companySignal()?.kyc?.enable) {
      if (
        this.companyStore.companySignal()?.kyc?.exato_digital?.key_id &&
        this.companyStore.companySignal()?.kyc?.method === KYCMethod.EXATO_DIGITAL
      ) {
        this.kycValidationService.addExatoDigitalToDocument();
      } else if (this.companyStore.companySignal()?.kyc?.method === KYCMethod.VERIFF) {
        this.kycValidationService.initVeriff();
      }
    }
  }

  private checkIFrame(company: { mode: string | null }): void {
    if (company.mode === 'IFRAME') {
      this.sportsbookIntegrationService.isIframe = true;
    }
  }

  private scrollTopGlobal() {
    if (!isDesktop()) {
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 10);
    }
  }

  private createDynamicRoute(): void {
    // const screens = this.viewBuilderConfigService.getScreenLayoutConfig().screens;
    // for (const screen of screens.filter((s) => s.enable)) {
    // this.appRoutingModule.addDynamicRoute(
    //   screen.route,
    //   () => import('./components/layout-dynamic-build/page-dynamic-layout.component').then((m) => m.PageDynamicLayoutComponent),
    //   screen,
    // );
    // }
  }
}
